import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiHeaders } from '../utils/apiHelpers';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Upload, 
  FileText, 
  Trash2, 
  Eye, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  XCircle,
  ChevronDown,
  ChevronUp,
  Euro,
  Search,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { ProgressoAnalisi, useMenuInAnalisi } from '../components/menu/AnalisiMenu';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const MenuPage = ({ tenantDb }) => {
  // Stati per la lista menu
  const [menus, setMenus] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(true);
  
  // Stati per upload
  const [uploadFile, setUploadFile] = useState(null);
  const [menuName, setMenuName] = useState('');
  const [menuDescription, setMenuDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Stati per visualizzazione items
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [menuItems, setMenuItems] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  
  // Stati per analisi
  const [analysisResults, setAnalysisResults] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  
  // Stati per parsing
  const [parsingMenuId, setParsingMenuId] = useState(null);

  // Helper per ottenere headers con supporto multi-tenant
  const getHeaders = (includeContentType = false) => {
    const token = localStorage.getItem('token') || localStorage.getItem('tenant_token');
    const headers = getApiHeaders();
    
    // Aggiungi header tenant se presente
    const storedTenantDb = tenantDb || localStorage.getItem('tenant_db');
    if (storedTenantDb) {
      headers['X-Tenant-DB'] = storedTenantDb.replace('tenant_', '');
    }
    
    if (includeContentType) {
      headers['Content-Type'] = 'multipart/form-data';
    }
    
    return headers;
  };

  // Definisco loadMenus con useCallback per usarlo come dependency
  const loadMenus = useCallback(async () => {
    setLoadingMenus(true);
    try {
      const response = await axios.get(`${API}/menus`, {
        headers: getHeaders()
      });
      setMenus(response.data);
    } catch (error) {
      console.error('Error loading menus:', error);
      
    } finally {
      setLoadingMenus(false);
    }
  }, [tenantDb]);

  // Carica menu all'avvio
  useEffect(() => {
    loadMenus();
  }, [loadMenus]);

  useMenuInAnalisi(menus, setMenus, () => getHeaders());

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Solo file PDF sono accettati');
        return;
      }
      setUploadFile(file);
      // Auto-fill name from filename
      if (!menuName) {
        setMenuName(file.name.replace('.pdf', '').replace(/_/g, ' '));
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !menuName.trim()) {
      toast.error('Seleziona un file PDF e inserisci un nome');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('nome', menuName.trim());
      formData.append('descrizione', menuDescription.trim());

      const response = await axios.post(`${API}/menus/upload`, formData, {
        headers: getHeaders(true)
      });

      toast.success(response.data.message);
      
      // Reset form
      setUploadFile(null);
      setMenuName('');
      setMenuDescription('');
      document.getElementById('menu-file-input').value = '';
      
      // Ricarica lista
      await loadMenus();
      
      // NON avviare parsing automatico - lascia scegliere all'utente
      // tra "Analizza" (AI lento) e "Vini" (regex veloce)
      toast.info('Menu caricato! Scegli "Analizza" per menu completi o "Vini" per carte vini.');
      
    } catch (error) {
      console.error('Error uploading menu:', error);
      toast.error(error.response?.data?.detail || 'Errore nel caricamento del menu');
    } finally {
      setUploading(false);
    }
  };

  const handleParseMenu = async (menuId) => {
    setParsingMenuId(menuId);
    try {
      const response = await axios.post(`${API}/menus/${menuId}/parse`, {}, {
        headers: getHeaders()
      });

      toast.info(response.data.message);
      await loadMenus();
      
    } catch (error) {
      console.error('Error parsing menu:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'analisi del menu');
    } finally {
      setParsingMenuId(null);
    }
  };

  // Parsing VELOCE per carte vini - senza AI
  const handleParseVini = async (menuId) => {
    setParsingMenuId(menuId);
    try {
      const response = await axios.post(`${API}/menus/${menuId}/parse-vini`, {}, {
        headers: getHeaders()
      });

      if (response.data.success) {
        toast.success(`${response.data.message}`);
      } else {
        toast.error('Errore nel parsing vini');
      }
      await loadMenus();
      
    } catch (error) {
      console.error('Error parsing vini:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'analisi della carta vini');
    } finally {
      setParsingMenuId(null);
    }
  };

  const handleDeleteMenu = async (menuId, menuName) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il menu "${menuName}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/menus/${menuId}`, {
        headers: getHeaders()
      });

      toast.success('Menu eliminato con successo');
      
      // Chiudi dettagli se era il menu selezionato
      if (selectedMenu?.id === menuId) {
        setSelectedMenu(null);
        setMenuItems(null);
      }
      
      await loadMenus();
      
    } catch (error) {
      console.error('Error deleting menu:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione del menu');
    }
  };

  const handleViewMenuItems = async (menu) => {
    if (selectedMenu?.id === menu.id) {
      // Toggle chiudi
      setSelectedMenu(null);
      setMenuItems(null);
      return;
    }

    setSelectedMenu(menu);
    setLoadingItems(true);
    
    try {
      const response = await axios.get(`${API}/menus/${menu.id}/items`, {
        headers: getHeaders()
      });

      setMenuItems(response.data);
      
      // Espandi tutte le categorie di default
      const cats = {};
      Object.keys(response.data.by_category || {}).forEach(cat => {
        cats[cat] = true;
      });
      setExpandedCategories(cats);
      
    } catch (error) {
      console.error('Error loading menu items:', error);
      
    } finally {
      setLoadingItems(false);
    }
  };

  const loadPriceAnalysis = async () => {
    setLoadingAnalysis(true);
    try {
      const response = await axios.get(`${API}/menus/analysis/price-comparison`, {
        headers: getHeaders()
      });
      setAnalysisResults(response.data);
    } catch (error) {
      console.error('Error loading analysis:', error);
      
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'uploaded':
        return <Badge className="bg-yellow-100 text-yellow-800">Caricato</Badge>;
      case 'parsing':
        return <Badge className="bg-blue-100 text-blue-800">Analisi in corso...</Badge>;
      case 'parsed':
        return <Badge className="bg-green-100 text-green-800">Analizzato</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-800">Errore</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploaded':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'parsing':
        return <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'parsed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <FileText className="h-5 w-5 text-gray-500" />;
    }
  };

  // Filtra items per ricerca
  const filteredItems = menuItems?.items?.filter(item => 
    !searchTerm || 
    item.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.categoria?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Raggruppa items filtrati per categoria
  const filteredByCategory = {};
  filteredItems.forEach(item => {
    const cat = item.categoria || 'Altro';
    if (!filteredByCategory[cat]) {
      filteredByCategory[cat] = [];
    }
    filteredByCategory[cat].push(item);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="h-7 w-7 text-orange-600" />
            Gestione Menu
          </h1>
          <p className="text-gray-600 mt-1">
            Carica i menu PDF del ristorante per analisi prezzi e confronto vendite
          </p>
        </div>
        <Button 
          onClick={loadPriceAnalysis}
          disabled={loadingAnalysis || menus.length === 0}
          className="bg-purple-600 hover:bg-purple-700"
        >
          {loadingAnalysis ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Analisi...
            </>
          ) : (
            <>
              <TrendingUp className="h-4 w-4 mr-2" />
              Analizza Prezzi
            </>
          )}
        </Button>
      </div>

      {/* Sezione Upload */}
      <Card className="border-2 border-dashed border-orange-300 hover:border-orange-500 transition-colors">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <Upload className="h-5 w-5" />
            Carica Nuovo Menu
          </CardTitle>
          <CardDescription>
            Carica un file PDF del menu. L&apos;AI estrarrà automaticamente articoli e prezzi.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* File Input */}
            <div className="space-y-2">
              <Label htmlFor="menu-file-input">File PDF</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-orange-400 transition-colors cursor-pointer"
                   onClick={() => document.getElementById('menu-file-input').click()}>
                <input
                  id="menu-file-input"
                  data-testid="menu-file-input"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {uploadFile ? (
                  <div className="space-y-1">
                    <FileText className="h-8 w-8 mx-auto text-orange-600" />
                    <p className="text-sm font-medium text-gray-900 truncate">{uploadFile.name}</p>
                    <p className="text-xs text-gray-500">{(uploadFile.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Upload className="h-8 w-8 mx-auto text-gray-400" />
                    <p className="text-sm text-gray-600">Clicca per selezionare</p>
                    <p className="text-xs text-gray-400">Solo file PDF</p>
                  </div>
                )}
              </div>
            </div>

            {/* Nome Menu */}
            <div className="space-y-2">
              <Label htmlFor="menu-name">Nome Menu *</Label>
              <Input
                id="menu-name"
                data-testid="menu-name-input"
                placeholder="Es: Menu Pranzo Inverno 2025"
                value={menuName}
                onChange={(e) => setMenuName(e.target.value)}
              />
              <p className="text-xs text-gray-500">Nome identificativo del menu</p>
            </div>

            {/* Descrizione */}
            <div className="space-y-2">
              <Label htmlFor="menu-desc">Descrizione (opzionale)</Label>
              <Input
                id="menu-desc"
                placeholder="Es: Menu pranzo feriali"
                value={menuDescription}
                onChange={(e) => setMenuDescription(e.target.value)}
              />
              <p className="text-xs text-gray-500">Note aggiuntive sul menu</p>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              onClick={handleUpload}
              disabled={uploading || !uploadFile || !menuName.trim()}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="menu-upload-btn"
            >
              {uploading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Caricamento...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Carica menu
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Alert Analisi Prezzi */}
      {analysisResults && (
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-purple-800">
              <TrendingUp className="h-5 w-5" />
              Risultati Analisi Prezzi
            </CardTitle>
            <CardDescription>
              Confronto tra prezzi del menu e vendite effettive ({analysisResults.periodo_analisi})
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-lg p-4 shadow-sm border">
                <div className="flex items-center gap-2 text-yellow-600 mb-2">
                  <AlertTriangle className="h-5 w-5" />
                  <span className="font-semibold">Non in Menu</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {analysisResults.summary?.articoli_non_in_menu || 0}
                </p>
                <p className="text-xs text-gray-500">Articoli venduti non presenti nei menu</p>
              </div>
              
              <div className="bg-white rounded-lg p-4 shadow-sm border">
                <div className="flex items-center gap-2 text-blue-600 mb-2">
                  <TrendingDown className="h-5 w-5" />
                  <span className="font-semibold">Prezzi Diversi</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {analysisResults.summary?.differenze_prezzo || 0}
                </p>
                <p className="text-xs text-gray-500">Articoli con differenza &gt;10%</p>
              </div>
              
              <div className="bg-white rounded-lg p-4 shadow-sm border">
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-semibold">Mai Venduti</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {analysisResults.summary?.articoli_mai_venduti || 0}
                </p>
                <p className="text-xs text-gray-500">Articoli menu senza vendite</p>
              </div>
            </div>

            {/* Alerts Details */}
            {analysisResults.alerts?.map((alert, idx) => (
              <div key={idx} className={`mb-4 p-4 rounded-lg border ${
                alert.severity === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                alert.severity === 'info' ? 'bg-blue-50 border-blue-200' :
                'bg-gray-50 border-gray-200'
              }`}>
                <h4 className="font-semibold text-gray-900 mb-2">{alert.messaggio}</h4>
                
                {alert.items?.length > 0 && (
                  <div className="max-h-48 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b">
                        <tr>
                          <th className="text-left p-2">Articolo</th>
                          {alert.tipo === 'differenze_prezzo' && (
                            <>
                              <th className="text-right p-2">Prezzo Menu</th>
                              <th className="text-right p-2">Prezzo Vendita</th>
                              <th className="text-right p-2">Diff %</th>
                            </>
                          )}
                          {alert.tipo === 'articoli_non_in_menu' && (
                            <>
                              <th className="text-right p-2">Qtà Venduta</th>
                              <th className="text-right p-2">Prezzo Medio</th>
                            </>
                          )}
                          {alert.tipo === 'articoli_mai_venduti' && (
                            <>
                              <th className="text-right p-2">Prezzo Menu</th>
                              <th className="text-left p-2">Categoria</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {alert.items.slice(0, 10).map((item, i) => (
                          <tr key={i} className="border-b hover:bg-white/50">
                            <td className="p-2 capitalize">{item.nome}</td>
                            {alert.tipo === 'differenze_prezzo' && (
                              <>
                                <td className="text-right p-2">€{item.prezzo_menu?.toFixed(2)}</td>
                                <td className="text-right p-2">€{item.prezzo_vendita_medio?.toFixed(2)}</td>
                                <td className={`text-right p-2 font-semibold ${
                                  item.differenza_percentuale > 0 ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {item.differenza_percentuale > 0 ? '+' : ''}{item.differenza_percentuale}%
                                </td>
                              </>
                            )}
                            {alert.tipo === 'articoli_non_in_menu' && (
                              <>
                                <td className="text-right p-2">{item.quantita_venduta}</td>
                                <td className="text-right p-2">€{item.prezzo_medio?.toFixed(2)}</td>
                              </>
                            )}
                            {alert.tipo === 'articoli_mai_venduti' && (
                              <>
                                <td className="text-right p-2">€{item.prezzo_menu?.toFixed(2)}</td>
                                <td className="p-2">{item.categoria}</td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {alert.items.length > 10 && (
                      <p className="text-xs text-gray-500 text-center mt-2">
                        ... e altri {alert.items.length - 10} articoli
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}

            <Button
              onClick={() => setAnalysisResults(null)}
              variant="outline"
              size="sm"
              className="mt-2"
            >
              Chiudi Analisi
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Lista Menu */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-gray-600" />
              Menu Caricati ({menus.length})
            </span>
            <Button
              onClick={loadMenus}
              variant="ghost"
              size="sm"
              disabled={loadingMenus}
            >
              <RefreshCw className={`h-4 w-4 ${loadingMenus ? 'animate-spin' : ''}`} />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingMenus ? (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 mx-auto text-gray-400 animate-spin" />
              <p className="text-gray-500 mt-2">Caricamento menu...</p>
            </div>
          ) : menus.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <BookOpen className="h-12 w-12 mx-auto text-gray-400" />
              <p className="text-gray-600 mt-2">Nessun menu caricato</p>
              <p className="text-sm text-gray-500">Carica il tuo primo menu PDF usando il form sopra</p>
            </div>
          ) : (
            <div className="space-y-3">
              {menus.map((menu) => (
                <div 
                  key={menu.id} 
                  className={`border rounded-lg p-4 transition-all ${
                    selectedMenu?.id === menu.id ? 'border-orange-500 bg-orange-50' : 'hover:border-gray-300'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-start gap-3">
                      {getStatusIcon(menu.status)}
                      <div>
                        <h3 className="font-semibold text-gray-900">{menu.nome}</h3>
                        {menu.descrizione && (
                          <p className="text-sm text-gray-600">{menu.descrizione}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 mt-1">
                          {getStatusBadge(menu.status)}
                          {menu.items_count > 0 && (
                            <Badge variant="outline">{menu.items_count} articoli</Badge>
                          )}
                          <span className="text-xs text-gray-500">
                            {new Date(menu.uploaded_at).toLocaleDateString('it-IT', {
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {menu.status === 'uploaded' && (
                        <>
                          {/* Pulsante Analizza standard (AI) */}
                          <Button
                            onClick={() => handleParseMenu(menu.id)}
                            disabled={parsingMenuId === menu.id}
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                            title="Analisi completa con AI (più lento)"
                            data-testid={`menu-analizza-${menu.id}`}
                          >
                            {parsingMenuId === menu.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              'Analizza'
                            )}
                          </Button>
                          {/* Pulsante Vini veloce (senza AI) */}
                          <Button
                            onClick={() => handleParseVini(menu.id)}
                            data-testid={`menu-vini-${menu.id}`}
                            disabled={parsingMenuId === menu.id}
                            size="sm"
                            variant="outline"
                            className="border-rose-300 text-rose-600 hover:bg-rose-50"
                            title="Parsing veloce per carte vini (senza AI)"
                          >
                            {parsingMenuId === menu.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              'Vini'
                            )}
                          </Button>
                        </>
                      )}
                      {menu.status === 'parsed' && (
                        <Button
                          onClick={() => handleViewMenuItems(menu)}
                          data-testid={`menu-vedi-${menu.id}`}
                          variant={selectedMenu?.id === menu.id ? 'default' : 'outline'}
                          size="sm"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          {selectedMenu?.id === menu.id ? 'Chiudi' : 'Vedi'}
                        </Button>
                      )}
                      <Button
                        onClick={() => handleDeleteMenu(menu.id, menu.nome)}
                        disabled={menu.status === 'parsing'}
                        title={menu.status === 'parsing' ? 'Attendi la fine dell\'analisi' : 'Elimina menu'}
                        data-testid={`menu-elimina-${menu.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {menu.status === 'parsing' && <ProgressoAnalisi menu={menu} />}
                  {menu.status === 'parsed' && menu.parse_message && (
                    <p className="mt-2 text-xs text-green-700" data-testid={`menu-esito-${menu.id}`}>{menu.parse_message}</p>
                  )}

                  {/* Errore parsing */}
                  {menu.status === 'error' && menu.parse_error && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid={`menu-errore-${menu.id}`}>
                      <p className="text-sm text-red-700">
                        <AlertTriangle className="h-4 w-4 inline mr-1" />
                        {menu.parse_error}
                      </p>
                      <Button
                        onClick={() => handleParseMenu(menu.id)}
                        size="sm"
                        variant="outline"
                        className="mt-2 text-red-600 border-red-300"
                        data-testid={`menu-riprova-${menu.id}`}
                      >
                        Riprova Analisi
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dettaglio Articoli Menu */}
      {selectedMenu && menuItems && (
        <Card className="border-orange-200">
          <CardHeader className="bg-orange-50">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-orange-600" />
                {selectedMenu.nome} - {menuItems.total_items} Articoli
              </span>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Cerca articolo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-48 sm:w-64"
                />
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {loadingItems ? (
              <div className="text-center py-8">
                <RefreshCw className="h-8 w-8 mx-auto text-gray-400 animate-spin" />
                <p className="text-gray-500 mt-2">Caricamento articoli...</p>
              </div>
            ) : Object.keys(filteredByCategory).length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <AlertCircle className="h-8 w-8 mx-auto text-gray-400" />
                <p className="text-gray-600 mt-2">
                  {searchTerm ? 'Nessun articolo trovato per la ricerca' : 'Nessun articolo nel menu'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(filteredByCategory).map(([categoria, items]) => (
                  <div key={categoria} className="border rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleCategory(categoria)}
                      className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <span className="font-semibold text-gray-900 flex items-center gap-2">
                        {categoria}
                        <Badge variant="outline" className="ml-2">{items.length}</Badge>
                      </span>
                      {expandedCategories[categoria] ? (
                        <ChevronUp className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      )}
                    </button>
                    
                    {expandedCategories[categoria] && (
                      <div className="divide-y">
                        {items.map((item, idx) => (
                          <div key={item.id || idx} className="flex items-center justify-between p-3 hover:bg-gray-50">
                            <span className="text-gray-800">{item.nome}</span>
                            <span className="font-semibold text-orange-600 flex items-center gap-1">
                              <Euro className="h-4 w-4" />
                              {item.prezzo?.toFixed(2) || '0.00'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MenuPage;
